import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CaseCarousel from './src/components/CaseCarousel';
import ImpactSection from './src/components/ImpactSection';
import Header from './src/components/Header';
import stImage from './src/img/st.png';

// Variables CSS para colores de marca
const brandColors = {
  '--stpp-green': '#00A86B',
  '--stpp-black': '#0B0F12',
  '--stpp-gray': '#111827',
  '--stpp-muted': '#6B7280',
  '--stpp-surface': '#F7F7F8',
};

// Aplicar variables CSS al documento
if (typeof document !== 'undefined') {
  const root = document.documentElement;
  Object.entries(brandColors).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

// Componente Container
const Container: React.FC<{ children: React.ReactNode; className?: string }> = ({ 
  children, 
  className = '' 
}) => (
  <div className={`max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);


// Componente Hero
const Hero: React.FC = () => {
  const { t } = useTranslation();
  
  const fadeUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  return (
    <section className="relative py-12 flex items-center bg-white min-h-[70vh]">
      <Container>
        <div className="grid grid-cols-12 gap-8 items-center">
          <motion.div 
            className="col-span-12 lg:col-span-6"
            {...fadeUp}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-8" 
                style={{ color: 'var(--stpp-black)' }}>
              {t('hero.title')}
            </h1>
            <motion.a
              href="#tally-open=wdBvLK&tally-layout=modal&tally-emoji-text=⚽&tally-emoji-animation=spin&tally-auto-close=1000"
              className="inline-flex items-center px-8 py-4 rounded-2xl text-lg font-semibold text-white transition-all duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-300"
              style={{ backgroundColor: 'var(--stpp-green)' }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t('hero.primaryCta')}
            </motion.a>
          </motion.div>
          
          <motion.div 
            className="col-span-12 lg:col-span-6"
            {...fadeUp}
            transition={{ delay: 0.2 }}
          >
            <div className="relative">
              <div className="w-full h-96 lg:h-[500px] rounded-2xl shadow-lg overflow-hidden">
                <img 
                  src={stImage} 
                  alt="ST++ - Expertos en tecnología y deportes"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

// Componente DualLensCard individual
const DualLensCard: React.FC<{
  title: string;
  tagline: string;
  industry: string[];
  technology: string[];
}> = ({ title, tagline, industry, technology }) => {
  const { t } = useTranslation();
  return (
    <div className="w-full">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--stpp-black)' }}>
          {title}
        </h2>
        <p className="text-xl" style={{ color: 'var(--stpp-muted)' }}>
          {tagline}
        </p>
      </div>

      <div className="grid grid-cols-12 gap-8 mb-8">
        {/* Industria */}
        <motion.div 
          className="col-span-12 lg:col-span-5 p-8 rounded-2xl shadow-lg bg-gray-100"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--stpp-black)' }}>
            {t('sections.industry')}
          </h3>
          <ul className="space-y-4">
            {industry.map((item, index) => (
              <li key={index} className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 mr-3 mt-0.5 text-green-600">✓</span>
                <span className={`text-gray-700 ${index === industry.length - 1 ? 'font-bold' : ''}`}>
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Conector visual */}
        <div className="col-span-12 lg:col-span-2 flex items-center justify-center">
          <div className="text-center">
            {/* Logo DDD */}
            <motion.div 
              className="w-16 h-16 mx-auto mb-3 rounded-full flex items-center justify-center shadow-lg" 
              style={{ backgroundColor: 'var(--stpp-green)' }}
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ duration: 0.3 }}
            >
              <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                {/* Arquitectura de dominios */}
                <rect x="4" y="4" width="6" height="6" rx="1" fill="white" opacity="0.8"/>
                <rect x="14" y="4" width="6" height="6" rx="1" fill="white" opacity="0.8"/>
                <rect x="4" y="14" width="6" height="6" rx="1" fill="white" opacity="0.8"/>
                <rect x="14" y="14" width="6" height="6" rx="1" fill="white" opacity="0.8"/>
                {/* Conexiones entre dominios */}
                <line x1="10" y1="7" x2="14" y2="7" stroke="white" strokeWidth="1.5"/>
                <line x1="7" y1="10" x2="7" y2="14" stroke="white" strokeWidth="1.5"/>
                <line x1="17" y1="10" x2="17" y2="14" stroke="white" strokeWidth="1.5"/>
                <line x1="10" y1="17" x2="14" y2="17" stroke="white" strokeWidth="1.5"/>
                {/* Punto central */}
                <circle cx="12" cy="12" r="2" fill="white"/>
              </svg>
            </motion.div>
            <div className="text-sm font-bold px-4 py-2 rounded-full bg-green-50 border-2 shadow-sm" style={{ 
              color: 'var(--stpp-green)',
              borderColor: 'var(--stpp-green)'
            }}>
              Domain Driven Design
            </div>
            <div className="text-xs mt-2 font-medium" style={{ color: 'var(--stpp-muted)' }}>
              Mismo idioma, distintas áreas
            </div>
          </div>
        </div>

        {/* Tecnología */}
        <motion.div 
          className="col-span-12 lg:col-span-5 p-8 rounded-2xl shadow-lg bg-gray-100"
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--stpp-black)' }}>
            {t('sections.technology')}
          </h3>
          <ul className="space-y-4">
            {technology.map((item, index) => (
              <li key={index} className="flex items-start">
                <span className="flex-shrink-0 w-6 h-6 mr-3 mt-0.5 text-blue-600">{ }</span>
                <span className={`text-gray-700 ${index === technology.length - 1 ? 'font-bold' : ''}`}>
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>

    </div>
  );
};

// Componente Carrusel
const SectionsCarousel: React.FC = () => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Obtener las secciones traducidas
  const sections = [
    {
      id: 'athletes',
      title: t('sections.athletes.title'),
      tagline: t('sections.athletes.tagline'),
      industry: t('sections.athletes.industry', { returnObjects: true }) as string[],
      technology: t('sections.athletes.technology', { returnObjects: true }) as string[]
    },
    {
      id: 'fans',
      title: t('sections.fans.title'),
      tagline: t('sections.fans.tagline'),
      industry: t('sections.fans.industry', { returnObjects: true }) as string[],
      technology: t('sections.fans.technology', { returnObjects: true }) as string[]
    },
    {
      id: 'executives',
      title: t('sections.executives.title'),
      tagline: t('sections.executives.tagline'),
      industry: t('sections.executives.industry', { returnObjects: true }) as string[],
      technology: t('sections.executives.technology', { returnObjects: true }) as string[]
    }
  ];

  // Navegación automática cada 3 segundos
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === sections.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, sections.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
    // Reanudar auto-play después de 5 segundos de inactividad
    setTimeout(() => setIsAutoPlaying(true), 5000);
  };

  const nextSlide = () => {
    const nextIndex = currentIndex === sections.length - 1 ? 0 : currentIndex + 1;
    goToSlide(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex = currentIndex === 0 ? sections.length - 1 : currentIndex - 1;
    goToSlide(prevIndex);
  };

  return (
    <section className="py-12 relative">
      <Container>
        {/* Controles de navegación */}
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={prevSlide}
            className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-300"
            aria-label="Sección anterior"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex space-x-3">
            {sections.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full carousel-dot focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-300 ${
                  index === currentIndex 
                    ? 'bg-green-600 active' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Ir a sección ${index + 1}`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-300"
            aria-label="Siguiente sección"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Contenido del carrusel */}
        <div className="carousel-container">
          <motion.div
            className="flex carousel-slide"
            animate={{ x: `-${currentIndex * 100}%` }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            {sections.map((section) => (
              <div key={section.id} className="w-full flex-shrink-0">
                <DualLensCard
                  title={section.title}
                  tagline={section.tagline}
                  industry={section.industry}
                  technology={section.technology}
                />
              </div>
            ))}
          </motion.div>
        </div>

        {/* Indicador de progreso */}
        <div className="mt-8 flex justify-center">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span>{currentIndex + 1}</span>
            <span>/</span>
            <span>{sections.length}</span>
          </div>
        </div>
      </Container>
    </section>
  );
};


// Componente Footer
const Footer: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <footer className="py-12" style={{ backgroundColor: 'var(--stpp-black)' }}>
      <Container>
        <div className="text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <svg width="74" height="84" viewBox="0 0 74 84" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-16 w-auto">
              <path d="M74 24.1416V35.5386C73.9998 34.4298 73.7093 33.3406 73.1576 32.3803C72.606 31.4201 71.8126 30.6227 70.8572 30.0683L40.1428 12.2439C39.1874 11.6891 38.1034 11.397 37 11.397C35.8966 11.397 34.8126 11.6891 33.8572 12.2439L3.14279 30.0683C2.18742 30.6227 1.39404 31.4201 0.842378 32.3803C0.290713 33.3406 0.000189751 34.4298 2.77811e-07 35.5386V24.1416C-0.000327812 23.0323 0.28995 21.9425 0.841641 20.9817C1.39333 20.0209 2.18699 19.223 3.14279 18.6684L33.8572 0.846887C34.8126 0.292092 35.8966 0 37 0C38.1034 0 39.1874 0.292092 40.1428 0.846887L70.8572 18.6684C71.813 19.223 72.6067 20.0209 73.1584 20.9817C73.7101 21.9425 74.0003 23.0323 74 24.1416Z" fill="white"/>
              <path d="M74 59.8583V48.4584C74.0003 49.5677 73.7101 50.6576 73.1584 51.6184C72.6067 52.5792 71.813 53.377 70.8572 53.9317L40.1428 71.7532C39.1874 72.308 38.1034 72.6 37 72.6C35.8966 72.6 34.8126 72.308 33.8572 71.7532L3.14279 53.9317C2.18742 53.3773 1.39404 52.5799 0.842378 51.6197C0.290713 50.6594 0.000189566 49.5702 9.31245e-08 48.4614V59.8583C-0.000189854 60.9668 0.290201 62.0558 0.84193 63.0156C1.39366 63.9755 2.18725 64.7722 3.14279 65.3257L33.8572 83.1531C34.8126 83.7079 35.8966 84 37 84C38.1034 84 39.1874 83.7079 40.1428 83.1531L70.8572 65.3257C71.8128 64.7722 72.6063 63.9755 73.1581 63.0156C73.7098 62.0558 74.0002 60.9668 74 59.8583V59.8583Z" fill="white"/>
              <path d="M4.49304 50.272L6.02611 45.4951H19.1781C20.3573 45.4951 21.2418 45.101 21.7666 44.0727C22.0309 43.6232 22.1761 43.113 22.1882 42.591C22.1882 42.1673 21.9169 42.0754 21.2831 42.0754H14.1808C10.0593 42.0784 8.19304 40.8042 8.19304 38.3238C8.19304 36.5695 8.94483 34.6641 10.0003 33.1232C11.6248 30.8829 14.2133 29.8635 18.3054 29.8635H32.4185L30.853 34.7056H17.8808C16.7016 34.7056 15.7139 35.2183 15.2275 36.2169C15.0114 36.6179 14.8875 37.0624 14.8648 37.5178C14.8648 38.0008 15.2569 38.0927 16.1591 38.0927H23.2112C27.3033 38.0927 29.0781 39.2425 29.0781 41.6013C29.0781 43.0533 28.5357 44.6565 27.7544 46.0789C26.1889 48.9504 23.5414 50.2809 19.2989 50.2809L4.49304 50.272Z" fill="white"/>
              <path d="M34.2227 50.272L39.214 34.6937H30.8175L32.4184 29.8546H55.7416L54.1408 34.6937H45.7443L40.753 50.272H34.2227Z" fill="white"/>
              <path d="M47.5988 47.5902L48.8665 43.6608H44.9955L45.7502 41.3079H49.6242L50.8919 37.3815H53.2505L51.9827 41.3079H55.8891L55.1344 43.6608H51.2368L49.9691 47.5902H47.5988Z" fill="white"/>
              <path d="M60.1168 47.5902L61.3846 43.6608H57.5106L58.2683 41.3079H62.1452L63.41 37.3815H65.7685L64.5038 41.3079H68.4101L67.6525 43.6608H63.7431L62.4754 47.5902H60.1168Z" fill="white"/>
            </svg>
          </div>
          
          {/* Información de contacto */}
          <div className="mb-8">
            <p className="text-gray-400 mb-2">
              {t('footer.address')}
            </p>
            <a 
              href="mailto:hola@stplusplus.com" 
              className="text-gray-400 hover:text-white transition-colors"
            >
              {t('footer.email')}
            </a>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-800">
            <p className="text-gray-500 text-sm">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </Container>
    </footer>
  );
};

// Componente FinalCta
const FinalCta: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <motion.section 
      className="py-12 text-center"
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true }}
    >
      <Container>
        <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--stpp-black)' }}>
        {t('finalCta.headline')}
        </h2>
        <motion.a
          href="#tally-open=wdBvLK&tally-layout=modal&tally-emoji-text=⚽&tally-emoji-animation=spin&tally-auto-close=1000"
          className="inline-flex items-center px-8 py-4 rounded-2xl text-lg font-semibold text-white transition-all duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-300"
          style={{ backgroundColor: 'var(--stpp-green)' }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {t('finalCta.primaryCta')}
        </motion.a>
      </Container>
    </motion.section>
  );
};

// Componente principal LandingPage
const LandingPage: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Meta tags para SEO */}
      <head>
        <title>ST++ - Expertos en tecnología y deportes</title>
        <meta name="description" content="Soluciones tecnológicas para el ecosistema deportivo." />
        <meta property="og:title" content="ST++ - Expertos en tecnología y deportes" />
        <meta property="og:description" content="Soluciones tecnológicas para el ecosistema deportivo." />
        <meta property="og:type" content="website" />
      </head>

      <Header />
      
      <main>
        <Hero />
        
        <ImpactSection />
        
        <SectionsCarousel />
        
        <CaseCarousel />
        
        {/* CTA final */}
        <FinalCta />
        
        <Footer />
      </main>
    </div>
  );
};

export default LandingPage;
